import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Bell, Calendar, Mail, Filter, Loader2, ChevronRight, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "Tout" },
  { key: "reservation", label: "Réservations" },
  { key: "contact", label: "Demandes" },
];

export default function AdminNotificationsHistory() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/notifications/history")
      .then((r) => setEvents(r.data.events || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const list = useMemo(
    () => (filter === "all" ? events : events.filter((e) => e.kind === filter)),
    [events, filter]
  );

  return (
    <div className="p-8 lg:p-10 max-w-5xl">
      <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">— Notifications</p>
      <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark mb-2">Historique complet</h1>
      <p className="text-sm text-fegray mb-8">Toutes les activités récentes : réservations et demandes de renseignements.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        <span className="inline-flex items-center gap-1.5 text-xs text-fegray uppercase tracking-wider font-semibold px-2">
          <Filter className="w-3.5 h-3.5" /> Filtres :
        </span>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`notif-history-filter-${f.key}`}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
              filter === f.key
                ? "bg-fegreen text-white shadow-sm"
                : "bg-white border border-gray-200 text-fegray-dark hover:border-fegreen"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-fegreen mx-auto" /></div>
      ) : list.length === 0 ? (
        <div className="fe-card p-12 text-center text-fegray">
          <Bell className="w-8 h-8 mx-auto text-fegray/30 mb-3" />
          Aucune notification pour le moment.
        </div>
      ) : (
        <div className="space-y-2" data-testid="notif-history-list">
          {list.map((ev) => <Row key={`${ev.kind}-${ev.id}`} ev={ev} />)}
        </div>
      )}
    </div>
  );
}

function Row({ ev }) {
  const isRes = ev.kind === "reservation";
  const meta = isRes ? getReservationMeta(ev) : getContactMeta(ev);
  const to = isRes ? `/admin/reservations?highlight=${ev.id}` : "/admin/contacts";
  return (
    <Link
      to={to}
      data-testid={`notif-history-row-${ev.id}`}
      className="fe-card p-4 flex items-center gap-4 hover:border-fegreen transition-all"
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${meta.bg}`}>
        <meta.Icon className={`w-4 h-4 ${meta.fg}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-bold text-fegray-dark text-sm truncate">{ev.title}</span>
          <span className={`text-[10px] uppercase tracking-wider font-bold ${meta.fg}`}>
            {meta.label}
          </span>
        </div>
        {ev.subtitle && (
          <div className="text-xs text-fegray truncate mt-0.5">
            {ev.subtitle}
            {isRes && ev.amount ? ` · ${Number(ev.amount).toFixed(2)}€` : ""}
          </div>
        )}
      </div>
      <div className="text-[10px] text-fegray/60 whitespace-nowrap text-right">
        {formatDate(ev.created_at)}
      </div>
      <ChevronRight className="w-4 h-4 text-fegray/40 shrink-0" />
    </Link>
  );
}

function getReservationMeta(ev) {
  if (ev.type === "reservation_paid") return { Icon: Calendar, bg: "bg-fegreen-light", fg: "text-fegreen", label: "Réservation payée" };
  if (ev.type === "reservation_cancelled") return { Icon: XCircle, bg: "bg-red-100", fg: "text-red-600", label: "Réservation annulée" };
  return { Icon: Clock, bg: "bg-amber-100", fg: "text-amber-700", label: "Réservation en attente" };
}

function getContactMeta(ev) {
  if (ev.status === "archived") return { Icon: Mail, bg: "bg-gray-100", fg: "text-fegray", label: "Demande archivée" };
  if (ev.status === "in_progress") return { Icon: Mail, bg: "bg-blue-100", fg: "text-blue-700", label: "Demande en cours" };
  if (ev.status === "handled") return { Icon: Mail, bg: "bg-fegreen-light", fg: "text-fegreen", label: "Demande traitée" };
  return { Icon: Mail, bg: "bg-amber-100", fg: "text-amber-700", label: "Nouvelle demande" };
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}
